/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { CreateAsyncThunk } from '@ibm-watson/shared/ts/state';
import { ChannelInstance } from '../../../../ts/channel';
import * as integrationsApi from '../../api/integrations';
import * as classicIntegrationsApi from '../../api/integrations/classic';
import * as integrationServerApi from '../../api/integrations/integrationServer';
import { subscriptionType } from '../../components/phone/phone-constants';
import { FIELDS as WEBCHAT_FIELDS } from '../../components/web-chat/schema';
import { PHONE_CHANNEL_TYPE } from '../../util/channelTypes';
import {
  createIntegration,
  deleteIntegration,
  updateIntegration
} from '../channels/actions';
import {
  createIntegration as createClassicIntegration,
  updateIntegration as updateClassicIntegration
} from '../channels/classic/actions';
import { selectAllIntegrations } from '../channels/selectors';
import { messages } from './messages';
import { selectAllServiceDesksByType } from './selectors';

interface LoadServiceDeskIntegrationProps {
  assistantId: string;
  environmentId?: string;
  integrationId: string;
  updateServiceDeskDraft?: boolean;
}
/**
 * Fetch and load an integration via an integration ID.
 *
 * @param {object} LoadServiceDeskIntegration
 * @returns IntegrationSchema
 */
export const loadServiceDeskIntegration = createAsyncThunk<
  any,
  LoadServiceDeskIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/loadServiceDeskIntegration',
  async (
    {
      assistantId,
      environmentId,
      integrationId,
      updateServiceDeskDraft = true
    },
    { dispatch }
  ) => {
    try {
      const serviceDesk = !_.isNil(environmentId)
        ? await integrationsApi.getIntegration(
            assistantId,
            environmentId,
            integrationId
          )
        : await classicIntegrationsApi.getIntegration(
            assistantId,
            integrationId
          );
      return { ...serviceDesk, updateServiceDeskDraft };
    } catch (error) {
      dispatch(
        addErrorMessage({
          message: messages.connectFailed,
          status: 'error',
          transactionId: error.transactionId
        })
      );
      throw error;
    }
  }
);

type UpdateServiceDeskIntegration = {
  assistantId: string;
  environmentId?: string | undefined;
  integrationId?: string;
  integration: any;
};
/**
 *
 *
 */
export const updateServiceDeskIntegration = createAsyncThunk<
  any,
  UpdateServiceDeskIntegration,
  CreateAsyncThunk
>(
  'integrations/updateServiceDeskIntegration',
  async (
    { assistantId, environmentId, integrationId, integration },
    { dispatch }
  ) => {
    try {
      const safeIntegrationId = integrationId ?? integration.integration_id;
      return !_.isNil(environmentId)
        ? integrationsApi.updateIntegration(
            assistantId,
            environmentId,
            safeIntegrationId,
            integration
          )
        : classicIntegrationsApi.updateIntegration(
            assistantId,
            safeIntegrationId,
            integration
          );
    } catch (error) {
      dispatch(addErrorMessage(error));
      // updatedObjectEvents
      throw error;
    }
  }
);

interface ConnectServiceDeskIntegrationProps {
  assistantId?: string;
  environmentId?: string;
  integration: any;
  data?: any;
}
export const connectServiceDeskIntegration = createAsyncThunk<
  any,
  ConnectServiceDeskIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/connectServiceDeskIntegration',
  async (
    { assistantId, environmentId, integration, data },
    { getState, dispatch }
  ) => {
    const safeAssistantId = assistantId ?? integration.assistant_id;
    const safeEnvironmentId = environmentId ?? integration.environment_id;

    try {
      let updatedIntegration;

      if (_.isNil(integration.integration_id)) {
        if (!_.isNil(safeEnvironmentId)) {
          // New IA doesn't allow multiples of the same service desk.
          // Remove any orphans of the same assistant, environment and type so folks don't get stuck.
          // We do this here because its too late to play defense out in the wild.
          const duplicateServiceDesks = selectAllServiceDesksByType(
            getState(),
            integration.type,
            safeAssistantId,
            safeEnvironmentId
          );
          if (duplicateServiceDesks?.length > 0) {
            for (let i = 0; i < duplicateServiceDesks.length; i++) {
              const duplicateServiceDesk = duplicateServiceDesks[i];
              await dispatch(
                deleteIntegration({
                  assistantId: safeAssistantId,
                  environmentId: safeEnvironmentId,
                  integration: duplicateServiceDesk
                })
              );
            }
          }
          updatedIntegration = await dispatch(
            createIntegration({
              assistantId: safeAssistantId,
              environmentId: safeEnvironmentId,
              integration,
              // do NOT load the service desk into redux state,
              // as that would overwrite the parent/webchat draft
              skipLoad: true
            })
          ).unwrap();
        } else {
          updatedIntegration = await dispatch(
            createClassicIntegration({
              assistantId: safeAssistantId,
              integration
            })
          ).unwrap();
        }
      } else {
        updatedIntegration = await dispatch(
          updateServiceDeskIntegration({
            assistantId: safeAssistantId,
            environmentId: safeEnvironmentId,
            integration
          })
        ).unwrap();
      }

      await integrationServerApi.subscribe(
        updatedIntegration.type,
        updatedIntegration.integration_id,
        data ?? {}
      );

      await loadServiceDeskIntegration({
        assistantId: safeAssistantId,
        environmentId: safeEnvironmentId,
        integrationId: updatedIntegration.integration_id
      });

      return updatedIntegration;
    } catch (error) {
      dispatch(
        addErrorMessage({
          message: messages.connectFailed,
          status: 'error',
          transactionId: error.transactionId
        })
      );
      throw error;
    }
  }
);

type DetachServiceDeskFromIntegrations = {
  assistantId: string;
  environmentId?: string | undefined;
  serviceDeskId: string;
};
/**
 *
 *
 */
export const detachServiceDeskFromIntegrations = createAsyncThunk<
  any,
  DetachServiceDeskFromIntegrations,
  CreateAsyncThunk
>(
  'integrations/detachServiceDeskFromIntegrations',
  async (
    { assistantId, environmentId, serviceDeskId },
    { dispatch, getState }
  ) => {
    try {
      const integrationsWithThisServiceDesk = selectAllIntegrations(
        getState()
      ).filter(
        integration =>
          _.get(
            integration.settings,
            WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_ID
          ) === serviceDeskId
      );

      for (const integration of integrationsWithThisServiceDesk) {
        const modifiedIntegration = _.cloneDeep(integration);

        _.set(
          modifiedIntegration,
          [
            'settings',
            ...WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_ID.split('.')
          ],
          ''
        );
        _.set(
          modifiedIntegration,
          [
            'settings',
            ...WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_TYPE.split('.')
          ],
          ''
        );
        _.set(
          modifiedIntegration,
          ['settings', ...WEBCHAT_FIELDS.SERVICE_DESK.IS_ON.split('.')],
          false
        );

        if (!_.isNil(environmentId)) {
          await dispatch(
            updateIntegration({
              assistantId,
              environmentId,
              integration: modifiedIntegration
            })
          );
        } else {
          await dispatch(
            updateClassicIntegration({
              assistantId,
              integration: modifiedIntegration
            })
          );
        }
      }
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const resetServiceDesk = createAction('integrations/resetServiceDesk');

type ConnectContactCenter = Partial<ChannelInstance> & {
  /**
   * access_key_id is used for subscribe for both Nice CXOne & Amazon Connect
   */
  access_key_id?: string;
  /**
   * access_key_secret is used for subscribe for both Nice CXOne & Amazon Connect
   */
  access_key_secret?: string;
  /**
   * arn is used for subscribe for just Amazon Connect
   */
  arn?: string;
  /**
   * api_key is used for subscribe for Genesys Audio Connect
   */
  api_key?: string;
  /**
   * client_secret is used for subscribe for Genesys Audio Connect
   */
  client_secret?: string;
  /**
   * type is either 'nice_cxone' or 'amazon_connect'
   */
  type: string;
  /**
   * integrationId is the phone integration's integration_id value
   */
  integrationId: string;
};

/**
 * Test connection with Contact Center
 * @param {string} access_key_id
 * @param {string} access_key_secret
 * @param {string} arn // only needed for Amazon Connect
 * @param {string} type
 * @param {string} integrationId
 * @param {string} api_key // only needed for Genesys audio connector
 * @param {string} client_secret // only needed for Genesys audio connector
 * @returns {object} subscribe response to the backend with updated payload
 */
export const connectContactCenter = createAsyncThunk<
  any,
  ConnectContactCenter,
  CreateAsyncThunk
>('channels/connectContactCenter', async params => {
  try {
    const {
      access_key_id,
      access_key_secret,
      api_key,
      client_secret,
      arn,
      type,
      integrationId
    } = params;
    const isAmazonConnect = type === subscriptionType.AMAZON_CONNECT;
    const isGenesysAudioConnect =
      type === subscriptionType.GENESYS_AUDIO_CONNECTOR_SUBSCRIBE;

    let data: Partial<ConnectContactCenter> = {
      access_key_id,
      access_key_secret,
      type
    };

    if (isAmazonConnect) {
      data = {
        access_key_id,
        access_key_secret,
        arn,
        type
      };
    } else if (isGenesysAudioConnect) {
      data = { api_key, client_secret, type };
    } else {
    }
    const subscribe = await integrationServerApi.subscribe(
      PHONE_CHANNEL_TYPE,
      integrationId,
      data
    );
    return subscribe;
  } catch (e) {
    throw e;
  }
});
